/**
 * @description
 * Response type definitions for the MediConnect Healthcare Information System
 * These interfaces define the structure of API responses, error formats,
 * and related types for consistent error handling throughout the application.
 */

/**
 * Standard API response structure
 */
export interface ApiResponse<T = any> {
  data: T;
  meta?: {
    pagination?: PaginationMeta;
    [key: string]: any;
  };
  statusCode?: number;
  timestamp?: string;
}

/**
 * Pagination metadata for paginated responses
 */
export interface PaginationMeta {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Standardized API error format
 */
export interface ApiError {
  statusCode: number;
  message: string;
  errors?: ErrorDetail[];
  timestamp: string;
  path?: string;
  code?: string;
}

/**
 * Detailed error information
 */
export interface ErrorDetail {
  field?: string;
  message: string;
  code?: string;
}

/**
 * Authentication token response
 */
export interface AuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

/**
 * Healthcare specific error codes
 */
export enum HealthcareErrorCode {
  DATA_ACCESS_DENIED = 'HC001',
  PHI_AUTHORIZATION_REQUIRED = 'HC002',
  INVALID_MEDICAL_DATA = 'HC003',
  EXPIRED_CREDENTIALS = 'HC004',
  HIPAA_VIOLATION = 'HC005',
  AUDIT_REQUIRED = 'HC006',
  PATIENT_NOT_FOUND = 'HC007',
  CONFLICTING_MEDICATION = 'HC008'
}

/**
 * Represents the structure of an error response from the API.
 */
export interface ApiErrorResponse {
  message?: string; // General error message
  detail?: string; // More specific detail, often used by frameworks
  errors?: { [key: string]: string[] } | string[]; // Field-specific validation errors or list of errors
  // Add other potential error fields based on backend framework (e.g., code, type)
}

/**
 * Represents a generic paginated API response structure.
 * Assumes pagination metadata is included alongside the data array.
 */
export interface PaginatedResponse<T> {
  data: T[]; // The actual data items for the current page
  meta: { // Metadata for pagination (adjust property names based on your API)
    currentPage: number;
    totalPages: number;
    totalItems?: number;
    itemsPerPage?: number;
  };
  links?: { // Optional HATEOAS links
    first?: string;
    last?: string;
    prev?: string;
    next?: string;
  };
} 